import React, { useEffect, useReducer, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";

import "./RequestForm.scss";
import { createOffPlatformShipment } from "api";
import errorToast from "helpers/errorToast";
import { FormStepTypes, FormStepValidationTypes } from "constants/";
import { initialState, reducer } from "./RequestFormState";
import Header from "./components/Header/Header";
import ScheduleForm from "./components/Schedule/ScheduleForm";
import FormConfirmation from "./components/Confirmation/FormConfirmation";
import CreateShipment from "./components/CreateShipment/CreateShipment";
import QuotationForm from "./components/CreateShipment/QuotationForm";
import { AddressProvider } from "../RequestForm1/components/Schedule/AddressProvider";

function NewShipment() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [offPlatformOptionalFields, setOffPlatformOptionalFields] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const methods = useForm<any>({
    defaultValues: {
      schedule: { pickup_cargo_ready: "after", delivery_cargo_ready: "before" },
    },
    shouldFocusError: true,
    mode: "all",
  });
  const { getValues, handleSubmit, trigger } = methods;
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const formStepRefs = {
    [FormStepTypes.SHIPMENT]: useRef<any>(null),
    [FormStepTypes.SCHEDULE]: useRef<any>(null),
  };

  useEffect(() => {
    if (!currentActiveStep) {
      searchParams.set("active_step", FormStepTypes.SHIPMENT);
      setSearchParams(searchParams);
    }
  }, []);

  const handleAccordionChange = (eventKey: string | null) => {
    if (!eventKey) {
      searchParams.delete("active_step");
    } else {
      searchParams.set("active_step", eventKey);
      if (currentActiveStep === "schedule") {
        trigger("schedule");
      }
      if (currentActiveStep === "quotation") {
        trigger("quotation");
      }
    }
    setSearchParams(searchParams);
  };

  const getScacCode = () => {
    if (getValues("shipment.tracking_scac_code") === "other" && getValues("shipment.other_tracking_scac_code")) {
      return getValues("shipment.other_tracking_scac_code");
    }
    return getValues("shipment.tracking_scac_code");
  };

  const formatType = (type: string) => {
    switch (type) {
      case "address":
        return "1";
      case "airport":
        return "2";
      case "seaport":
        return "3";
      default:
        return "";
    }
  };

  const shipmentFormData: any = {
    title: getValues("shipment.title"),
    location: getValues("shipment.location"),
    freight_mode: getValues("shipment.freight_mode"),
    provider: getValues("shipment.provider"),
    tracking_type: getValues("shipment.tracking_type"),
    tracking_scac_code: getScacCode(),
    tracking_number: getValues("shipment.tracking_number"),
    custom_longfield_01: getValues("shipment.custom_longfield_01"),
    pickup_airport_code: getValues("schedule.pickup_airport_code"),
    pickup_airport: getValues("schedule.pickup_airport"),
    pickup_seaport: getValues("schedule.pickup_seaport"),
    pickup_seaport_code: getValues("schedule.pickup_seaport_code"),
    pickup_address_name: getValues("schedule.pickup_address_name"),
    pickup_street: getValues("schedule.pickup_street"),
    pickup_street_line_two: getValues("schedule.pickup_street_line2"),
    pickup_zipcode: getValues("schedule.pickup_zipcode"),
    pickup_city: getValues("schedule.pickup_city"),
    pickup_country: getValues("schedule.pickup_country"),
    pickup_contact_name: getValues("schedule.pickup_contact_name"),
    pickup_contact_phone: getValues("schedule.pickup_contact_phone"),
    pickup_contact_email: getValues("schedule.pickup_contact_email"),
    pickup_address_type: formatType(getValues("schedule.pickup_address_type")),
    pickup_address_reference_number: getValues("schedule.pickup_address_reference_number"),
    pickup_state: getValues("schedule.pickup_state"),
    destination_airport_code: getValues("schedule.stop1_airport_code"),
    destination_airport: getValues("schedulestop1_airport"),
    destination_seaport_code: getValues("schedule.stop1_seaport_code"),
    destination_seaport: getValues("schedule.stop1_seaport"),
    destination_address_name: getValues("schedule.stop1_address_name"),
    destination_state: getValues("schedule.stop1_state"),
    destination_street: getValues("schedule.stop1_street"),
    destination_street_line_two: getValues("schedule.stop1_street_line2"),
    destination_zipcode: getValues("schedule.stop1_zipcode"),
    destination_city: getValues("schedule.stop1_city"),
    destination_country: getValues("schedule.stop1_country"),
    delivery_contact_name: getValues("schedule.stop1_contact_name"),
    delivery_contact_phone: getValues("schedule.stop1_contact_phone"),
    delivery_contact_email: getValues("schedule.stop1_contact_email"),
    delivery_address_type: formatType(getValues("schedule.stop1_address_type")),
    destination_address_reference_number: getValues("schedule.destination_address_reference_number"),
    price: getValues("quotation.price"),
    weight: getValues("quotation.weight") ? getValues("quotation.weight") : "",
    volume: getValues("quotation.volume") ? getValues("quotation.volume") : "",
    currency_code: getValues("quotation.currency_code"),
    chargeable_weight: getValues("quotation.chargeable_weight"),
  };

  const validateSection = async (sectionName: string) => {
    let isCustomValidated = true;
    if (formStepRefs[sectionName]?.current?.checkStepValidation) {
      isCustomValidated = formStepRefs[sectionName].current.checkStepValidation();
    }
    const isValid = await methods.trigger(FormStepValidationTypes[sectionName]);
    return isValid && isCustomValidated;
  };

  const validateStepAndFocusOnError = async () => {
    let isValidate = true;
    const stepPriorityOrder = [FormStepTypes.SHIPMENT, FormStepTypes.SCHEDULE];
    // eslint-disable-next-line no-restricted-syntax
    for (const stepName of stepPriorityOrder) {
      // eslint-disable-next-line no-await-in-loop
      const isValid = await validateSection(stepName);
      if (!isValid) {
        handleAccordionChange(stepName);
        isValidate = false;
        break;
      }
    }
    return isValidate;
  };

  const onSubmit = async () => {
    const isValid = await validateStepAndFocusOnError();
    if (!isValid) {
      return;
    }
    setIsSubmitting(true);
    // remove all empty fields and empty strings from formdata
    Object.keys(shipmentFormData).forEach((key) => {
      if (shipmentFormData[key] === "" || shipmentFormData[key] === undefined) {
        delete shipmentFormData[key];
      }
    });

    // Remove the state if the type is not "address"
    if (shipmentFormData.pickup_address_type !== "1") delete shipmentFormData.pickup_state;
    if (shipmentFormData.delivery_address_type !== "1") delete shipmentFormData.destination_state;

    createOffPlatformShipment(shipmentFormData)
      .then((res) => {
        navigate(`/shipment/${res.data.shipment_reference_number}/tracking`);
      })
      .catch((err) => errorToast(err))
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onError = () => {};

  const handleSubmitClick = () => {
    if (submitBtnRef.current) {
      submitBtnRef.current.click();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onError)} className="request-form" id="request-form">
        <Header state={state} isQuoteRequest={false} isOffPlatformShipment />
        <Accordion onSelect={handleAccordionChange} activeKey={currentActiveStep || ""}>
          <CreateShipment state={state} dispatch={dispatch} isQuoteRequest={false} />
          <AddressProvider>
            <ScheduleForm
              isOffPlatformShipment
              formState={state}
              setOffPlatformOptionalFields={setOffPlatformOptionalFields}
              dispatch={dispatch}
              isQuoteRequest={false}
            />
          </AddressProvider>
          {offPlatformOptionalFields && <QuotationForm offPlatformOptionalFields={offPlatformOptionalFields} />}
          <FormConfirmation
            isSubmitting={isSubmitting}
            state={state}
            isQuoteRequest={false}
            onSubmitClick={handleSubmitClick}
            offPlatformOptionalFields={offPlatformOptionalFields}
          />
        </Accordion>
        <button ref={submitBtnRef} type="submit" className="form-submit">
          submit
        </button>
      </form>
    </FormProvider>
  );
}

export default NewShipment;
